/**
 * Auth Guard — Learning Frontend
 *
 * Mirrors the SSO-aware pattern used by LMS / PRO / WWUS / MMI frontends:
 *
 * 1. Already authenticated (NGXS token in storage) → allow through.
 * 2. Staging/production + id_token cookie present → redirect to /auth/callback
 *    (SsoCallbackComponent will exchange it for a Learning JWT).
 * 3. Staging/production + no cookie → redirect to auth-ui with returnTo param.
 * 4. Local dev → fall back to the legacy /account/validate-login route.
 *
 * ---
 *
 * Garde d'authentification — Frontend Learning
 *
 * 1. Déjà authentifié (jeton NGXS en stockage) → autoriser.
 * 2. Staging/prod + cookie id_token présent → rediriger vers /auth/callback.
 * 3. Staging/prod + pas de cookie → rediriger vers l'auth-ui avec returnTo.
 * 4. Dev local → repli sur /account/validate-login.
 */

import { assertInInjectionContext, inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AuthState } from '@tsin-core/states/auth.state';
import { ConfigService } from '@tsin-core/services/config.service';

/** Read a cookie value by name (returns '' if not found). */
function getCookie(name: string): string {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
}

const canActivateFn: CanActivateFn = (route, state) => {
  assertInInjectionContext(canActivateFn);

  const router        = inject(Router);
  const store         = inject(Store);
  const configService = inject(ConfigService);

  // 1. Already authenticated — let through.
  // 1. Déjà authentifié — autoriser.
  if (store.selectSnapshot(AuthState.getIsAuthenticated)) {
    return true;
  }

  const env       = configService.getEnvironment();  // 'dev' | 'stage' | 'prod'
  const authUiUrl = configService.getAuthUiUrl();

  // 2 & 3. Non-local environments use the centralized SSO login.
  // Detect the locale from the requested route so that /fr users are
  // sent to the French auth-ui login page and returned to /fr/auth/callback.
  //
  // 2 & 3. Environnements non-locaux : connexion SSO centralisée.
  // La locale est détectée depuis l'URL pour envoyer les francophones vers
  // la page de connexion française et les rediriger vers /fr/auth/callback.
  if (env !== 'dev' && authUiUrl) {
    const isFr = state.url === '/fr' || state.url.startsWith('/fr/');

    // 2. id_token cookie already set by auth-ui → exchange it.
    // 2. Cookie id_token déjà positionné par l'auth-ui → l'échanger.
    if (getCookie('id_token')) {
      const callbackPath = isFr ? '/fr/auth/callback' : '/auth/callback';
      console.log('[authGuard] id_token cookie found — redirecting to', callbackPath);
      return router.parseUrl(callbackPath);
    }

    // 3. No session and no cookie → send user to auth-ui.
    // 3. Aucune session ni cookie → envoyer l'utilisateur vers l'auth-ui.
    const callbackPath = isFr ? '/fr/auth/callback' : '/auth/callback';
    const callbackUrl  = `${window.location.origin}${callbackPath}`;
    const loginPath    = isFr ? '/fr/login' : '/login';
    const loginUrl     = `${authUiUrl}${loginPath}?returnTo=${encodeURIComponent(callbackUrl)}`;

    console.log('[authGuard] Redirecting to auth-ui:', loginUrl);
    window.location.href = loginUrl;
    return false;
  }

  // 4. Local dev — use the legacy email/password login form.
  // 4. Dev local — utiliser le formulaire de connexion classique.
  return router.parseUrl('/account/validate-login');
};

export const authGuard: CanActivateFn = (route, state) => canActivateFn(route, state);

